import { useCallback, useState } from 'react';
import { useEventCallback } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import {
  SetupAuthenticator,
  RequestedAuthenticator,
  ConfirmAuthenticator,
  AuthenticatorConfigSerialized,
  PublicMFAConfigHelpers,
  AuthenticatorMfaRequest,
  ConfirmAuthenticatorSerialized,
  ConfirmMfaSerialized,
} from '@playq/octopus2-auth';
import { UserID } from '@playq/octopus-common';

import {
  useConfirmUserMFA,
  useDisableUserMFA,
  useRequestMFA,
  useDisableMFA,
  useRequestResetMFACodes,
  useConfirmResetMFACodes,
} from '/api';
import { snackbarService } from '/common/snackbarService';
import { authToolkit } from '/store';
import { QRCode, RecoveryInput, useMFAInput, useRecoveryCodes } from '/common/MFA';

export type UseMFAOptions = {
  onMutate?: (userId: UserID) => void;
};

export const useMFA = (options?: UseMFAOptions) => {
  const dispatch = useDispatch();

  const authState = useSelector(authToolkit.selectors.state);

  const [token, setToken] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [mfa, setMFA] = useState<{ [key: string]: AuthenticatorConfigSerialized }>();
  const [qrCodeUrl, setQRCodeUrl] = useState('');
  const [recoveryInputOpen, setRecoveryInputOpen] = useState(false);

  const { onMutate } = options ?? {};

  const updateMFA = (updated: { [key: string]: AuthenticatorConfigSerialized } | undefined) => {
    dispatch(authToolkit.actions.updateMfa(updated));
  };

  const { mutate: requestMFA } = useRequestMFA({
    onSuccess: (res) => {
      if (res instanceof RequestedAuthenticator) {
        setQRCodeUrl(res.qrCodeLink);
        setToken(res.token);
      }
    },
  });

  const { mutate: disableUserMFA } = useDisableUserMFA({
    onSuccess: () => {
      snackbarService.success('MFA disabled.');
      setMFA(undefined);
      updateMFA(undefined);
    },
  });

  const { mutate: confirmMFA } = useConfirmUserMFA({
    onSuccess: (res) => {
      setRecoveryCodes(res.codes);
      updateMFA(mfa);
    },
  });

  const { mutate: disableMFA } = useDisableMFA({
    onSuccess: (_d, userId) => {
      snackbarService.success('MFA disabled.');
      onMutate?.(userId);
    },
  });

  const [RecoveryCodes, openRecoveryCodes] = useRecoveryCodes();

  const { mutate: confirmResetCodes } = useConfirmResetMFACodes({
    onSuccess: (codes) => {
      openRecoveryCodes(codes?.codes ?? []);
    },
  });

  const handleResetCodes = (auth: ConfirmMfaSerialized) => {
    const payload = auth as ConfirmAuthenticatorSerialized;
    confirmResetCodes(new ConfirmAuthenticator(payload));
  };

  const [MFAInput, openMFAInput] = useMFAInput({
    onSubmit: handleResetCodes,
  });

  const { mutate: requestResetCodes } = useRequestResetMFACodes({
    onSuccess: (res) => {
      if (res instanceof AuthenticatorMfaRequest) {
        openMFAInput(res.token);
      }
    },
  });

  const handleRequestMFA = useCallback(() => {
    const updatedMFA = new SetupAuthenticator();
    requestMFA(updatedMFA);
    setMFA(PublicMFAConfigHelpers.serialize(updatedMFA));
  }, [requestMFA]);

  const handleSubmitQR = useEventCallback((pass: string) => {
    confirmMFA(new ConfirmAuthenticator({ token, pass }));
  });

  const handleClear = useCallback(() => {
    setQRCodeUrl('');
    setRecoveryCodes([]);
  }, []);

  const handleDisableUserMFA = useCallback(() => {
    setRecoveryInputOpen(true);
  }, []);

  const closeRecoveryInput = useCallback(() => {
    setRecoveryInputOpen(false);
  }, []);

  const toggleMFA = useCallback(() => {
    if (authState.mfa) {
      handleDisableUserMFA();
    } else {
      handleRequestMFA();
    }
  }, [authState.mfa, handleDisableUserMFA, handleRequestMFA]);

  const QR = <QRCode url={qrCodeUrl} recoveryCodes={recoveryCodes} onSubmit={handleSubmitQR} onClear={handleClear} />;

  const RecoveryCodesInput = (
    <RecoveryInput open={recoveryInputOpen} onSubmit={disableUserMFA} onClose={closeRecoveryInput} />
  );

  return {
    mfaEnabled: !!authState.mfa,
    QRCode: QR,
    RecoveryInput: RecoveryCodesInput,
    MFAInput,
    RecoveryCodes,
    toggleMFA,
    disableUserMFA: handleDisableUserMFA,
    disableMFA,
    resetCodes: requestResetCodes,
  };
};
